import {
  useCallback,
  useEffect,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type RefObject,
} from 'react';

export function useKeyboardInteraction({
  canNavigateBack,
  closeMenu,
  isOpen,
  navigateBack,
  surfaceRef,
}: {
  canNavigateBack?: boolean;
  closeMenu: () => void;
  isOpen: boolean;
  navigateBack?: () => void;
  surfaceRef: RefObject<HTMLDivElement>;
}) {
  const [focusedItemIndex, setFocusedItemIndex] = useState<number>(0);

  const getInteractiveItems = useCallback((): Array<
    HTMLAnchorElement | HTMLButtonElement
  > => {
    return Array.from(
      surfaceRef.current?.querySelectorAll<
        HTMLAnchorElement | HTMLButtonElement
      >(
        '.popup-menu-action-control-classes, .popup-menu-close-button-classes'
      ) || []
    );
  }, [surfaceRef]);

  useEffect(() => {
    if (!isOpen) {
      setFocusedItemIndex(0);
      return;
    }

    const interactiveItems = getInteractiveItems();

    if (interactiveItems.length === 0) return;

    const boundedIndex = Math.min(
      focusedItemIndex,
      interactiveItems.length - 1
    );

    if (boundedIndex !== focusedItemIndex) {
      setFocusedItemIndex(boundedIndex);
    }

    const activeElement = document.activeElement as HTMLElement | null;
    const isFocusWithinSurface = Boolean(
      activeElement && surfaceRef.current?.contains(activeElement)
    );
    const hasExternalFocus = Boolean(
      activeElement && activeElement !== document.body && !isFocusWithinSurface
    );

    if (hasExternalFocus) {
      return;
    }

    interactiveItems[boundedIndex]?.focus();
  });

  const handleMenuKeyDown = (
    event: ReactKeyboardEvent<HTMLDivElement>
  ): void => {
    const interactiveItems = getInteractiveItems();
    const totalItems = interactiveItems.length;

    if (totalItems === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setFocusedItemIndex((prev) => {
        const boundedPrev = Math.min(prev, totalItems - 1);
        return (boundedPrev + 1) % totalItems;
      });
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setFocusedItemIndex((prev) => {
        const boundedPrev = Math.min(prev, totalItems - 1);
        return (boundedPrev - 1 + totalItems) % totalItems;
      });
      return;
    }

    if (event.key === 'Home') {
      event.preventDefault();
      setFocusedItemIndex(0);
      return;
    }

    if (event.key === 'End') {
      event.preventDefault();
      setFocusedItemIndex(totalItems - 1);
      return;
    }

    if (event.key === 'Enter') {
      const activeElement = document.activeElement;
      const selectedItem = interactiveItems.find(
        (item) => item === activeElement
      );

      if (!selectedItem) return;

      event.preventDefault();
      selectedItem.click();
      return;
    }

    if (event.key === 'ArrowRight') {
      const activeElement = document.activeElement as HTMLElement | null;

      if (activeElement?.dataset.popupHasChildren !== 'true') return;

      event.preventDefault();
      activeElement.click();
      return;
    }

    if (event.key === 'ArrowLeft') {
      if (!canNavigateBack || !navigateBack) return;

      event.preventDefault();
      navigateBack();
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      closeMenu();
    }
  };

  return {
    handleMenuKeyDown,
  };
}
